import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import MasterFlowUiConfigFields from './MasterFlowDrawer/MasterFlowUiConfigFields';
import {
  defaultMasterFlowUiConfig,
  flowUiCatalogFromLookups,
  parseMasterFlowUiConfig,
} from '../services/masterFlowUiConfig';

/**
 * License Integration allowlists — pick which master connectors/flows tenants may use.
 * For each licensed flow, configure Sync/Hub ⋮ actions and Properties sections for that tenant.
 */
const LicenseIntegrationAssets = ({
  hubEnabled = true,
  connectorCatalog = [],
  flowCatalog = [],
  licensedConnectorIds,
  licensedFlowIds,
  licensedFlowUiByMfId = {},
  flowUiLookups = {},
  onChangeConnectors,
  onChangeFlows,
  onChangeFlowUi,
  maxConnectors,
  maxFlows,
  readOnly = false,
}) => {
  const [addKind, setAddKind] = useState(null); // 'connector' | 'flow' | null
  const [search, setSearch] = useState('');
  const [draftIds, setDraftIds] = useState(() => new Set());
  const [uiEditMfId, setUiEditMfId] = useState(null);
  const [uiEditDraft, setUiEditDraft] = useState(() => defaultMasterFlowUiConfig());

  const catalogForMfId = (mfId) => {
    const flow = (flowCatalog || []).find((f) => String(f.mfId) === String(mfId));
    return flowUiCatalogFromLookups(flowUiLookups, flow?.mfFlowFunction || '');
  };

  const connectorRows = useMemo(() => {
    const byId = new Map((connectorCatalog || []).map((c) => [String(c.mcId), c]));
    return [...licensedConnectorIds]
      .map((id) => {
        const c = byId.get(id);
        return {
          id,
          name: c?.mcName || id,
          type: c?.mcConnectorType || '',
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [connectorCatalog, licensedConnectorIds]);

  const flowRows = useMemo(() => {
    const byId = new Map((flowCatalog || []).map((f) => [String(f.mfId), f]));
    return [...licensedFlowIds]
      .map((id) => {
        const f = byId.get(id);
        return {
          id,
          name: f?.mfName || id,
          type: f?.mfFlowFunction || '',
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [flowCatalog, licensedFlowIds]);

  const seedUiForMfId = (mfId) => {
    const catalog = catalogForMfId(mfId);
    if (licensedFlowUiByMfId?.[mfId]) {
      return parseMasterFlowUiConfig(licensedFlowUiByMfId[mfId], catalog);
    }
    return defaultMasterFlowUiConfig(catalog);
  };

  const openAdd = (kind) => {
    setAddKind(kind);
    setSearch('');
    setDraftIds(new Set());
  };

  const closeAdd = () => {
    setAddKind(null);
    setDraftIds(new Set());
    setSearch('');
  };

  const catalogForDialog = useMemo(() => {
    if (addKind === 'connector') {
      return (connectorCatalog || [])
        .filter((c) => c.mcId && !licensedConnectorIds.has(String(c.mcId)))
        .map((c) => ({
          id: String(c.mcId),
          name: c.mcName || c.mcId,
          type: c.mcConnectorType || '',
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (addKind === 'flow') {
      return (flowCatalog || [])
        .filter((f) => f.mfId && !licensedFlowIds.has(String(f.mfId)))
        .map((f) => ({
          id: String(f.mfId),
          name: f.mfName || f.mfId,
          type: f.mfFlowFunction || '',
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return [];
  }, [addKind, connectorCatalog, flowCatalog, licensedConnectorIds, licensedFlowIds]);

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogForDialog;
    return catalogForDialog.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        String(r.type || '')
          .toLowerCase()
          .includes(q)
    );
  }, [catalogForDialog, search]);

  const confirmAdd = () => {
    if (draftIds.size === 0) return;
    if (addKind === 'connector') {
      const next = new Set(licensedConnectorIds);
      draftIds.forEach((id) => next.add(id));
      onChangeConnectors?.(next);
    } else if (addKind === 'flow') {
      const next = new Set(licensedFlowIds);
      draftIds.forEach((id) => next.add(id));
      onChangeFlows?.(next);
      const uiNext = { ...(licensedFlowUiByMfId || {}) };
      draftIds.forEach((id) => {
        if (!uiNext[id]) {
          uiNext[id] = seedUiForMfId(id);
        }
      });
      onChangeFlowUi?.(uiNext);
    }
    closeAdd();
  };

  const removeConnector = (id) => {
    const next = new Set(licensedConnectorIds);
    next.delete(id);
    onChangeConnectors?.(next);
  };

  const removeFlow = (id) => {
    const next = new Set(licensedFlowIds);
    next.delete(id);
    onChangeFlows?.(next);
    if (licensedFlowUiByMfId && Object.prototype.hasOwnProperty.call(licensedFlowUiByMfId, id)) {
      const uiNext = { ...licensedFlowUiByMfId };
      delete uiNext[id];
      onChangeFlowUi?.(uiNext);
    }
  };

  const openFlowUi = (id) => {
    setUiEditMfId(id);
    setUiEditDraft(seedUiForMfId(id));
  };

  const saveFlowUi = () => {
    if (!uiEditMfId) return;
    const catalog = catalogForMfId(uiEditMfId);
    onChangeFlowUi?.({
      ...(licensedFlowUiByMfId || {}),
      [uiEditMfId]: parseMasterFlowUiConfig(uiEditDraft, catalog),
    });
    setUiEditMfId(null);
  };

  const uiEditCatalog = uiEditMfId ? catalogForMfId(uiEditMfId) : { kind: 'SYNC', actions: [], sections: [] };

  const toggleDraft = (id) => {
    setDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setDraftIds(new Set(filteredCatalog.map((r) => r.id)));
  };

  const clearDraft = () => setDraftIds(new Set());

  const parseLimit = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const connectorLimit = parseLimit(maxConnectors);
  const flowLimit = parseLimit(maxFlows);

  const uiEditFlowName =
    flowRows.find((r) => r.id === uiEditMfId)?.name || uiEditMfId || '';

  const AssetPanel = ({
    title,
    count,
    limit,
    emptyHint,
    addLabel,
    onAdd,
    rows,
    onRemove,
    typeLabel,
    showFlowUi = false,
  }) => {
    const overLimit = limit != null && count > limit;
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          px={1.5}
          py={1}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
        >
          <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {title}
            </Typography>
            <Chip
              size="small"
              label={limit != null ? `${count} / ${limit} types` : `${count} selected`}
              color={overLimit ? 'warning' : 'default'}
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          </Stack>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!hubEnabled || readOnly}
            onClick={onAdd}
          >
            {addLabel}
          </Button>
        </Box>

        {overLimit && (
          <Alert severity="warning" sx={{ borderRadius: 0, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
            More types selected than the instance limit above ({limit}). Tenants can still only create up to{' '}
            {limit} instance{limit === 1 ? '' : 's'}.
          </Alert>
        )}

        {rows.length === 0 ? (
          <Box px={2} py={2.5} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {emptyHint}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
            {rows.map((r, idx) => (
              <ListItem
                key={r.id}
                dense
                divider={idx > 0}
                secondaryAction={
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    {showFlowUi && (
                      <Tooltip title="Row menu (⋮) and Properties for this tenant">
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            disabled={!hubEnabled || readOnly}
                            onClick={() => openFlowUi(r.id)}
                            aria-label={`UI for ${r.name}`}
                          >
                            <TuneIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove from license">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          disabled={!hubEnabled || readOnly}
                          onClick={() => onRemove(r.id)}
                          aria-label={`Remove ${r.name}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
                sx={{ py: 0.75, pr: 10 }}
              >
                <ListItemText
                  primary={r.name}
                  secondary={r.type ? `${typeLabel}: ${r.type}` : null}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true, title: r.name }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    noWrap: true,
                    title: r.type,
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  };

  const isConnector = addKind === 'connector';

  return (
    <Box sx={{ width: '100%', mt: 1.5, opacity: hubEnabled ? 1 : 0.55 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Included connectors &amp; flows
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Pick which Master Connector / Master Flow types this license includes. For each flow, use the
        tune icon to set which ⋮ actions and Properties sections this tenant sees. Save the license
        to apply.
      </Typography>

      {!hubEnabled && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          Enable the Hub product to edit connector and flow inclusions.
        </Alert>
      )}

      <Stack spacing={1.5}>
        <AssetPanel
          title="Connectors"
          count={connectorRows.length}
          limit={connectorLimit}
          emptyHint="None yet. Click Add to choose from Master Connector."
          addLabel="Add"
          onAdd={() => openAdd('connector')}
          rows={connectorRows}
          onRemove={removeConnector}
          typeLabel="Type"
        />
        <AssetPanel
          title="Flows"
          count={flowRows.length}
          limit={flowLimit}
          emptyHint="None yet. Click Add to choose from Master Flow."
          addLabel="Add"
          onAdd={() => openAdd('flow')}
          rows={flowRows}
          onRemove={removeFlow}
          typeLabel="Path"
          showFlowUi
        />
      </Stack>

      <Dialog open={!!addKind} onClose={closeAdd} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          {isConnector ? 'Add connectors' : 'Add flows'}
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {isConnector
              ? 'Select master connectors to include on this license.'
              : 'Select master flows to include on this license. You can set ⋮ / Properties per flow after adding.'}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={isConnector ? 'Search by name or type…' : 'Search by name or path…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              {filteredCatalog.length} available
              {draftIds.size > 0 ? ` · ${draftIds.size} selected` : ''}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Button size="small" onClick={selectAllVisible} disabled={filteredCatalog.length === 0}>
                Select all
              </Button>
              <Button size="small" onClick={clearDraft} disabled={draftIds.size === 0}>
                Clear
              </Button>
            </Stack>
          </Box>

          {filteredCatalog.length === 0 ? (
            <Box py={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                {catalogForDialog.length === 0
                  ? 'Everything from the catalog is already on this license.'
                  : 'No matches for your search.'}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto', mx: -0.5 }}>
              {filteredCatalog.map((r) => {
                const checked = draftIds.has(r.id);
                return (
                  <ListItemButton
                    key={r.id}
                    dense
                    selected={checked}
                    onClick={() => toggleDraft(r.id)}
                    sx={{ borderRadius: 1, mb: 0.25 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" size="small" checked={checked} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText
                      primary={r.name}
                      secondary={r.type || null}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true, title: r.name }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        noWrap: true,
                        title: r.type,
                        sx: { display: 'block' },
                      }}
                      sx={{ my: 0, minWidth: 0 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={closeAdd}>Cancel</Button>
          <Button variant="contained" onClick={confirmAdd} disabled={draftIds.size === 0}>
            Add{draftIds.size > 0 ? ` (${draftIds.size})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!uiEditMfId}
        onClose={() => setUiEditMfId(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 1.5, maxWidth: 420 } }}
      >
        <DialogTitle sx={{ py: 1.25, px: 2 }}>
          <Typography component="span" variant="subtitle1" fontWeight={600} display="block" lineHeight={1.3}>
            Flow UI
            {uiEditCatalog.kind ? ` · ${uiEditCatalog.kind === 'HUB' ? 'Hub' : 'Sync'}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap title={uiEditFlowName}>
            {uiEditFlowName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2, py: 1.25 }}>
          <MasterFlowUiConfigFields
            value={uiEditDraft}
            onChange={setUiEditDraft}
            actions={uiEditCatalog.actions || []}
            sections={uiEditCatalog.sections || []}
            surfaceKind={uiEditCatalog.kind}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1, gap: 0.5 }}>
          <Button size="small" onClick={() => setUiEditMfId(null)} color="inherit">
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={saveFlowUi} disabled={readOnly}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LicenseIntegrationAssets;
